/**
 * Read-only floating details popup for imported ICS calendar events (FULL_VISIBILITY feeds).
 */

import React from 'react';
import { Card, Typography, Button, Flex, Tag, Tooltip, theme } from 'antd';
import {
  CalendarOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { CalendarExternalAppointment } from '@/api/mcp-appointments';
import { formatAppointmentTimeRange } from './calendarUtils';
import { AppointmentDetailRow } from '../manage/AppointmentDetailRow';
import { AppointmentDetailsBox } from '../manage/AppointmentDetailsBox';
import { ModalTitle } from '@/components/ui/ModalTitle';

const { Text } = Typography;

interface ExternalCalendarEventDetailsPopupProps {
  event: CalendarExternalAppointment;
  resourceName: string;
  visible: boolean;
  isPositioned?: boolean;
  floatingStyles?: React.CSSProperties;
  floatingRef?: (node: HTMLElement | null) => void;
  onClose: () => void;
}

export const ExternalCalendarEventDetailsPopup: React.FC<ExternalCalendarEventDetailsPopupProps> = ({
  event,
  resourceName,
  visible,
  isPositioned = true,
  floatingStyles,
  floatingRef,
  onClose
}) => {
  const { token } = theme.useToken();
  const { t, i18n } = useTranslation('manage');

  if (!visible) return null;

  const summary =
    event.normalizedSummary?.trim() ||
    t('appointmentComponents.externalEvent.details.noSummary');
  const hasLocation = Boolean(event.normalizedLocation?.trim());
  const timeRange = formatAppointmentTimeRange(event.startTime, event.endTime, i18n.language);

  return (
    <Card
      ref={floatingRef}
      style={{
        ...floatingStyles,
        visibility: isPositioned ? 'visible' : 'hidden',
        zIndex: (token.zIndexPopupBase ?? 1000) + 20,
        width: 'min(400px, calc(100vw - 24px))',
        maxHeight: 'calc(100vh - 32px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary
      }}
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 } }}
      variant="outlined">
      <Flex
        align="flex-start"
        justify="space-between"
        gap={token.marginSM}
        style={{
          padding: `${token.paddingSM}px ${token.paddingMD}px`,
          borderBottom: `1px solid ${token.colorBorderSecondary}`
        }}>
        <ModalTitle
          icon={<CalendarOutlined />}
          title={t('appointmentComponents.externalEvent.details.title')}
          subtitle={timeRange}
          tone="neutral"
        />
        <Tooltip title={t('appointmentComponents.details.close')}>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label={t('appointmentComponents.details.close')}
            onClick={onClose}
          />
        </Tooltip>
      </Flex>

      <Flex vertical gap={token.marginSM} style={{ padding: token.paddingMD, overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <div>
          <Tag color="green" style={{ margin: 0 }}>
            {t('appointmentComponents.externalEvent.details.readOnly')}
          </Tag>
        </div>

        <AppointmentDetailsBox>
          <AppointmentDetailRow
            icon={<FileTextOutlined />}
            label={t('appointmentComponents.externalEvent.details.summary')}>
            <Text strong>{summary}</Text>
          </AppointmentDetailRow>
          {hasLocation ? (
            <AppointmentDetailRow
              icon={<EnvironmentOutlined />}
              label={t('appointmentComponents.externalEvent.details.location')}>
              <Text>{event.normalizedLocation}</Text>
            </AppointmentDetailRow>
          ) : null}
          <AppointmentDetailRow icon={<HomeOutlined />} label={t('appointmentComponents.externalEvent.details.resource')}>
            <Text>{resourceName}</Text>
          </AppointmentDetailRow>
          <AppointmentDetailRow
            icon={<CalendarOutlined />}
            label={t('appointmentComponents.externalEvent.details.dateTime')}>
            <Text>{timeRange}</Text>
          </AppointmentDetailRow>
        </AppointmentDetailsBox>
      </Flex>
    </Card>
  );
};
